// Lib
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';

// Utils
import { navigateToUrl } from '../../reducers/navigationActions';
import { RerouteDeepLinkPlugin } from '../../../capacitor_plugins/rerouteDeepLink';
import { CAPACITOR_REROUTE_DEEP_LINK_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { withPayloadLogging } from '../capacitorPayloadLogging';

// Check if the URL needs to be hit first and then use the resulting URL (i.e. for redirects)
const shouldResolveUrl = (url: string): boolean => {
    return (
        url.includes('.milanote.com/api/auth/email/verify') || // email verification
        url.startsWith('https://links.milanote.com') || // links in daily digest, notifications, etc
        url.includes('account.milanote.com/subscription-invite#code=') // claim pro account
    );
};

const isBoardUrl = (url: string): boolean => {
    // The board ID is matched for 14 to 15 characters because there was some issue with element ID creation at some point
    // that caused ID creations with an extra character, which now exists in the database.
    // This regex also matches an optional permission ID ("p") query parameter.
    const boardUrlRegex =
        /^https:\/\/(app|staging)\.milanote\.com\/[a-zA-Z0-9]{14,15}(?:\/.*)?(?:\?p=[a-zA-Z0-9]{11})?\/?$/;
    return boardUrlRegex.test(url);
};

// Check if the URL should just be used as is.
const shouldDirectlyNavigate = (url: string): boolean => {
    return (
        url.includes('/change-password?code=') || // password reset
        isBoardUrl(url)
    );
};

export default function () {
    const dispatch = useDispatch();

    useEffect(() => {
        if (!Capacitor.isPluginAvailable('App')) return;

        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            const url = event.url;
            if (shouldResolveUrl(url)) {
                if (!Capacitor.isPluginAvailable('CapacitorHttp')) return;
                CapacitorHttp.get({ url }).then((response) => {
                    const resolvedUrl = response.url;
                    dispatch(navigateToUrl(resolvedUrl));
                });
            } else if (shouldDirectlyNavigate(url)) {
                dispatch(navigateToUrl(url));
            } else {
                if (!Capacitor.isPluginAvailable(CAPACITOR_REROUTE_DEEP_LINK_PLUGIN)) return;
                withPayloadLogging(
                    `${CAPACITOR_REROUTE_DEEP_LINK_PLUGIN}.openUrlInExternalBrowserWithoutLoopingBackToApp`,
                    (payload) => RerouteDeepLinkPlugin.openUrlInExternalBrowserWithoutLoopingBackToApp(payload),
                    { url },
                );
            }
        });
    }, []);
}
